import _classCallCheck from "/near-peer-app/nearpeer-frontend/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/near-peer-app/nearpeer-frontend/node_modules/@babel/runtime/helpers/esm/createClass.js";
import { noopReturn, defaults, isEasingGenerator, isEasingList, interpolate } from '@motionone/utils';
import { getEasingFunction } from './utils/easing.es.js';
var Animation = /*#__PURE__*/function () {
  function Animation(output) {
    var _this = this;
    var keyframes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 1];
    var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      easing = _ref.easing,
      _ref$duration = _ref.duration,
      initialDuration = _ref$duration === void 0 ? defaults.duration : _ref$duration,
      _ref$delay = _ref.delay,
      delay = _ref$delay === void 0 ? defaults.delay : _ref$delay,
      _ref$endDelay = _ref.endDelay,
      endDelay = _ref$endDelay === void 0 ? defaults.endDelay : _ref$endDelay,
      _ref$repeat = _ref.repeat,
      repeat = _ref$repeat === void 0 ? defaults.repeat : _ref$repeat,
      offset = _ref.offset,
      _ref$direction = _ref.direction,
      direction = _ref$direction === void 0 ? "normal" : _ref$direction,
      _ref$autoplay = _ref.autoplay,
      autoplay = _ref$autoplay === void 0 ? true : _ref$autoplay;
    _classCallCheck(this, Animation);
    this.startTime = null;
    this.rate = 1;
    this.t = 0;
    this.cancelTimestamp = null;
    this.easing = noopReturn;
    this.duration = 0;
    this.totalDuration = 0;
    this.repeat = 0;
    this.playState = "idle";
    this.finished = new Promise(function (resolve, reject) {
      _this.resolve = resolve;
      _this.reject = reject;
    });
    easing = easing || defaults.easing;
    if (isEasingGenerator(easing)) {
      var custom = easing.createAnimation(keyframes);
      easing = custom.easing;
      keyframes = custom.keyframes || keyframes;
      initialDuration = custom.duration || initialDuration;
    }
    this.repeat = repeat;
    this.easing = isEasingList(easing) ? noopReturn : getEasingFunction(easing);
    this.updateDuration(initialDuration);
    var interpolate$1 = interpolate(keyframes, offset, isEasingList(easing) ? easing.map(getEasingFunction) : noopReturn);
    this.tick = function (timestamp) {
      var _a;
      // TODO: Temporary fix for OptionsResolver typing
      delay = delay;
      var t = 0;
      if (_this.pauseTime !== undefined) {
        t = _this.pauseTime;
      } else {
        t = (timestamp - _this.startTime) * _this.rate;
      }
      _this.t = t;
      // Convert to seconds
      t /= 1000;
      // Rebase on delay
      t = Math.max(t - delay, 0);
      /**
       * If this animation has finished, set the current time
       * to the total duration.
       */
      if (_this.playState === "finished" && _this.pauseTime === undefined) {
        t = _this.totalDuration;
      }
      /**
       * Get the current progress (0-1) of the animation. If t is >
       * than duration we'll get values like 2.5 (midway through the
       * third iteration)
       */
      var progress = t / _this.duration;
      // TODO progress += iterationStart
      /**
       * Get the current iteration (0 indexed). For instance the floor of
       * 2.5 is 2.
       */
      var currentIteration = Math.floor(progress);
      /**
       * Get the current progress of the iteration by taking the remainder
       * so 2.5 is 0.5 through iteration 2
       */
      var iterationProgress = progress % 1.0;
      if (!iterationProgress && progress >= 1) {
        iterationProgress = 1;
      }
      /**
       * If iteration progress is 1 we count that as the end
       * of the previous iteration.
       */
      iterationProgress === 1 && currentIteration--;
      /**
       * Reverse progress if we're not running in "normal" direction
       */
      var iterationIsOdd = currentIteration % 2;
      if (direction === "reverse" || direction === "alternate" && iterationIsOdd || direction === "alternate-reverse" && !iterationIsOdd) {
        iterationProgress = 1 - iterationProgress;
      }
      var p = t >= _this.totalDuration ? 1 : Math.min(iterationProgress, 1);
      var latest = interpolate$1(_this.easing(p));
      output(latest);
      var isAnimationFinished = _this.pauseTime === undefined && (_this.playState === "finished" || t >= _this.totalDuration + endDelay);
      if (isAnimationFinished) {
        _this.playState = "finished";
        (_a = _this.resolve) === null || _a === void 0 ? void 0 : _a.call(_this, latest);
      } else if (_this.playState !== "idle") {
        _this.frameRequestId = requestAnimationFrame(_this.tick);
      }
    };
    if (autoplay) this.play();
  }
  return _createClass(Animation, [{
    key: "play",
    value: function play() {
      var now = performance.now();
      this.playState = "running";
      if (this.pauseTime !== undefined) {
        this.startTime = now - this.pauseTime;
      } else if (!this.startTime) {
        this.startTime = now;
      }
      this.cancelTimestamp = this.startTime;
      this.pauseTime = undefined;
      this.frameRequestId = requestAnimationFrame(this.tick);
    }
  }, {
    key: "pause",
    value: function pause() {
      this.playState = "paused";
      this.pauseTime = this.t;
    }
  }, {
    key: "finish",
    value: function finish() {
      this.playState = "finished";
      this.tick(0);
    }
  }, {
    key: "stop",
    value: function stop() {
      var _a;
      this.playState = "idle";
      if (this.frameRequestId !== undefined) {
        cancelAnimationFrame(this.frameRequestId);
      }
      (_a = this.reject) === null || _a === void 0 ? void 0 : _a.call(this, false);
    }
  }, {
    key: "cancel",
    value: function cancel() {
      this.stop();
      this.tick(this.cancelTimestamp);
    }
  }, {
    key: "reverse",
    value: function reverse() {
      this.rate *= -1;
    }
  }, {
    key: "commitStyles",
    value: function commitStyles() {}
  }, {
    key: "updateDuration",
    value: function updateDuration(duration) {
      this.duration = duration;
      this.totalDuration = duration * (this.repeat + 1);
    }
  }, {
    key: "currentTime",
    get: function get() {
      return this.t;
    },
    set: function set(t) {
      if (this.pauseTime !== undefined || this.rate === 0) {
        this.pauseTime = t;
      } else {
        this.startTime = performance.now() - t / this.rate;
      }
    }
  }, {
    key: "playbackRate",
    get: function get() {
      return this.rate;
    },
    set: function set(rate) {
      this.rate = rate;
    }
  }]);
}();
export { Animation };